import { CRITERIA, SECTIONS, getCriteriaBySection } from './criteria';
import { isFinding, visibilityMap } from './evaluationIntegrity';
import { isScorableCriterion } from './checklistSave';

const LEGACY_BY_ID = new Map(CRITERIA.map((criterion) => [criterion.id, criterion]));

/** #Fase1-10 — Nunca se identifica una sección por su título: puede repetirse. */
export function sectionKey(section, index = 0) {
  return String(section.section_key ?? section.id ?? `unknown-section-${index}`);
}

export function sectionTitle(section) {
  return section.section_name || section.title || section.section_id || 'Sección';
}

function evaluationSections(evaluation) {
  if (evaluation?.sections?.length) return evaluation.sections;
  return Object.values(SECTIONS).map((key) => ({
    section_key: key,
    section_name: key,
    criteria: getCriteriaBySection(key).map((criterion) => ({
      ...criterion,
      criterion_id: criterion.id,
      ...(evaluation?.items || []).find((item) => item.criterio_id === criterion.id),
    })),
  }));
}

/** #Fase1-10 — Legacy conserva el catálogo, pero los flags del GET tienen precedencia. */
export function legacyCriterion(evaluation, criterion) {
  const metadata = (evaluation.sections || []).flatMap(section => section.criteria || [])
    .find(row => String(row.criterion_id) === String(criterion.id));
  return {
    question_type: 'score_scale', scoring_enabled: true,
    ...criterion, ...metadata,
    id: criterion.id,
    blocker: metadata?.is_blocking ?? criterion.blocker,
  };
}

/** Conserva cada sección, aunque comparta título con otra o todavía no tenga resumen. */
export function getEvalSections(evaluation) {
  if (!evaluation) return [];

  const sections = evaluationSections(evaluation);
  const stats = new Map(
    (evaluation.score_summary?.section_stats || [])
      .filter((stat) => stat.section_key != null)
      .map((stat) => [String(stat.section_key), stat])
  );
  const visibility = visibilityMap(sections.flatMap((section) => section.criteria || []));
  let number = 0;
  return sections.map((section, index) => {
    const key = sectionKey(section, index);
    const stat = stats.get(key);
    const rows = (section.criteria || [])
      .filter(
        (criterion) =>
          criterion.visible !== false &&
          visibility[criterion.question_id ?? criterion.criterion_id] !== false
      )
      .map((criterion, rowIndex) => {
        number += 1;
        const legacy = LEGACY_BY_ID.get(criterion.criterion_id);
        const item = {
          ...criterion,
          notes: criterion.notes ?? criterion.response_notes,
          evidence_urls: criterion.evidence_urls?.length
            ? criterion.evidence_urls
            : (criterion.evidence || []).map((entry) => (typeof entry === 'string' ? entry : entry?.file_url))
                .filter(Boolean),
        };
        return {
          key: criterion.question_id ?? criterion.criterion_id ?? `${key}-${rowIndex}`,
          number,
          criterion: {
            ...criterion,
            id: criterion.criterion_id ?? criterion.question_id ?? number,
            title: criterion.title || criterion.criterion_text || legacy?.title || '',
            focus: criterion.focus || criterion.description || legacy?.focus || '',
            blocker: criterion.is_blocking ?? criterion.blocker ?? legacy?.blocker ?? false,
            recintoSeguro: criterion.recinto_seguro || legacy?.recintoSeguro || '',
            descriptions: criterion.descriptions || legacy?.descriptions,
            recommendations: criterion.recommendations || legacy?.recommendations,
            scoringEnabled: isScorableCriterion(criterion),
          },
          item,
        };
      });
      return {
      key,
      name: sectionTitle(section),
      score: stat?.raw ?? '—',
      max: stat?.max ?? '—',
      percentage: stat?.percentage ?? null,
      weight: stat?.weight ?? section.weight,
        rows,
      };
    });
  }

/** Estadísticas por section_key; title solo se usa como etiqueta de tablas y gráficos. */
export function getSectionStatsFromEval(evaluation) {
  if (!evaluation) return {};
  if (!evaluation.sections?.length && evaluation.score_summary?.section_stats?.length) {
    return Object.fromEntries(
      evaluation.score_summary.section_stats.map((stat, index) => [
        sectionKey(stat, index),
        {
          title: stat.title,
          score: stat.raw,
          max: stat.max,
          percentage: stat.percentage,
          weight: stat.weight,
        },
      ])
    );
  }
  return Object.fromEntries(
    getEvalSections(evaluation).map((section) => [
      section.key,
      {
        title: section.name,
        score: section.score,
        max: section.max,
        percentage: section.percentage,
        weight: section.weight,
      },
    ])
  );
}

/** #Fase1-10 — is_finding es la única pertenencia a hallazgos; severidad no agrega reactivos. */
export function getEvalIncidents(evaluation, meta = {}) {
  return getEvalSections(evaluation).flatMap((section) =>
    section.rows.filter((row) => isFinding(row.criterion, row.item))
      .map((row) => {
        const critical = row.criterion.blocker && row.item.score === 0;
        return {
        id: `${evaluation.id}-${row.key}`,
          severity: critical ? 'Crítico' : 'Mayor',
        criterionId: row.number,
        criterionNumber: row.number,
        title: row.criterion.title,
          description: row.criterion.focus,
          blocker: row.criterion.blocker,
          section: section.name,
          score: row.item.score,
        notes: row.item.notes,
          recommendation: row.item.recommendation || row.item.auto_recommendation || '',
        evidence_urls: row.item.evidence_urls || [],
        ...meta,
        };
      })
  );
}
